/**
 * SafeLocalStorage - Utility for robust localStorage/sessionStorage operations with error handling
 * 
 * Security feature: Uses sessionStorage for sensitive auth data (clears on browser close)
 * Uses localStorage for non-sensitive persistent data
 * 
 * Handles common storage issues:
 * - JSON parsing errors from corrupted data
 * - Browser privacy modes where storage is disabled
 * - Storage quota exceeded errors
 * - Automatic cleanup of corrupted data
 */

class SafeLocalStorage {
  /**
   * Determine which storage to use based on key name
   * Auth/session data uses sessionStorage (more secure, clears on close)
   * Other data uses localStorage (persists)
   */
  static _getStorage(key) {
    // Use sessionStorage for sensitive auth/session data
    if (key.includes('auth') || key.includes('session') || key.includes('token')) {
      return sessionStorage;
    }
    // Use localStorage for everything else
    return localStorage;
  }

  /**
   * Safely get an item from storage with JSON parsing
   * @param {string} key - Storage key
   * @param {any} defaultValue - Value to return if key doesn't exist or parsing fails
   * @returns {any} Parsed value or defaultValue
   */
  static get(key, defaultValue = null) {
    try {
      const storage = this._getStorage(key);
      const item = storage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item);
    } catch (error) {
      console.warn(`SafeLocalStorage.get() failed for key "${key}":`, error.message);
      console.warn('Cleaning up corrupted data...');
      
      // Clean up corrupted data
      this.remove(key);
      return defaultValue;
    }
  }

  /**
   * Safely set an item in storage with JSON stringification
   * @param {string} key - Storage key
   * @param {any} value - Value to store (will be JSON.stringified)
   * @returns {boolean} Success status
   */
  static set(key, value) {
    try {
      const storage = this._getStorage(key);
      const stringValue = JSON.stringify(value);
      storage.setItem(key, stringValue);
      return true;
    } catch (error) {
      console.error(`SafeLocalStorage.set() failed for key "${key}":`, error.message);
      
      if (error.name === 'QuotaExceededError') {
        console.warn('Storage quota exceeded. Consider implementing cleanup strategy.');
      }
      
      return false;
    }
  }

  /**
   * Safely remove an item from storage
   * @param {string} key - Storage key to remove
   * @returns {boolean} Success status
   */
  static remove(key) {
    try {
      const storage = this._getStorage(key);
      storage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`SafeLocalStorage.remove() failed for key "${key}":`, error.message);
      return false;
    }
  }

  /**
   * Safely clear all localStorage (use with caution)
   * @returns {boolean} Success status
   */
  static clear() {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('SafeLocalStorage.clear() failed:', error.message);
      return false;
    }
  }

  /**
   * Check if localStorage is available and working
   * @returns {boolean} Availability status
   */
  static isAvailable() {
    try {
      const testKey = '__safe_localStorage_test__';
      const testValue = 'test';
      localStorage.setItem(testKey, testValue);
      const retrieved = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      return retrieved === testValue;
    } catch (error) {
      console.warn('localStorage is not available:', error.message);
      return false;
    }
  }

  /**
   * Get multiple items at once
   * @param {Array<{key: string, defaultValue?: any}>} items - Array of key/defaultValue pairs
   * @returns {Object} Object with keys as properties
   */
  static getMultiple(items) {
    const result = {};
    items.forEach(({ key, defaultValue = null }) => {
      result[key] = this.get(key, defaultValue);
    });
    return result;
  }

  /**
   * Set multiple items at once
   * @param {Object} items - Object with key/value pairs
   * @returns {boolean} Success status for all operations
   */
  static setMultiple(items) {
    let allSuccessful = true;
    Object.entries(items).forEach(([key, value]) => {
      if (!this.set(key, value)) {
        allSuccessful = false;
      }
    });
    return allSuccessful;
  }

  /**
   * Get all keys that match a prefix (useful for cleanup)
   * @param {string} prefix - Key prefix to match
   * @returns {Array<string>} Array of matching keys
   */
  static getKeysByPrefix(prefix) {
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keys.push(key);
        }
      }
      return keys;
    } catch (error) {
      console.error('SafeLocalStorage.getKeysByPrefix() failed:', error.message);
      return [];
    }
  }

  /**
   * Clean up all keys with a specific prefix
   * @param {string} prefix - Key prefix to remove
   * @returns {number} Number of keys removed
   */
  static clearByPrefix(prefix) {
    const keys = this.getKeysByPrefix(prefix);
    let removedCount = 0;
    
    keys.forEach(key => {
      if (this.remove(key)) {
        removedCount++;
      }
    });
    
    return removedCount;
  }
}

export default SafeLocalStorage;